import React, { useEffect, useMemo, useState } from 'react';
import {
  Row,
  Col,
  styled,
  ThemeProps,
  Image,
  Body,
  Currency,
  Shape,
  Caption,
} from '@teko/ds-desktop';
import { CartItem } from '@teko/cart/v2';
import { ProductNoteInput, ProductPrice } from '../../shared';
import { CartLine } from '../CartLine';
import { t } from 'i18n';
import CartComboHeader from './CartComboHeader';
import { useCartV2, useTenantConfig } from 'contexts';
import { debounce } from '@teko/utils';
import { calculateProductComboQuantity } from 'helpers';
import { getTrackingData } from '@teko/tracking';
import { Quantity } from 'components/Quantity';
import { ITEM_UPDATE_DEBOUNCE_TIME } from '../../utils/constants';

interface CartComboListProps extends React.HTMLAttributes<HTMLDivElement> {
  item: CartItem;
  onChangeComboProduct: (baseTotalProduct: number) => void;
  index: number;
}

const StyledChangeComboButton = styled(Body)(
  ({ theme }: ThemeProps) => `
  cursor: pointer;
  color: ${theme?.colors.link500};
  font-weight: 400;
`,
);

const ComboPromotionItemImage = styled(Image)`
  max-width: 40px;
  max-height: 40px;
`;

const StyledCartLine = styled(CartLine)`
  background-color: transparent;
  padding: 1rem 0;
  .applied-promotion {
    background-color: white;
  }
`;

const CartComboList = (props: CartComboListProps) => {
  const { item, onChangeComboProduct, index, ...tracking } = props;

  const {
    onRemoveItem,
    onSelectItem,
    onChangeQuantity,
    isUpdatingItemQuantity,
  } = useCartV2();
  const [comboQuantity, setComboQuantity] = useState(item.quantity || 0);

  const { epicOmni1553 } = useTenantConfig();

  const childItems = item.childItems || [];
  const promotionItems = item.promotions || [];
  const currentTotalProduct = calculateProductComboQuantity(childItems);
  const comboPromotion =
    item.promotions &&
    item.promotions.length > 0 &&
    item.promotions[0].promotions.length > 0
      ? item.promotions[0].promotions[0].promotionId
      : undefined;

  const headerTitle = useMemo(() => {
    return item.totalPromotionValue ? (
      <div>
        {t('Đã tiết kiệm')}{' '}
        <Currency value={item.totalPromotionValue} weight="bold" />
      </div>
    ) : (
      <></>
    );
  }, [item.totalPromotionValue]);

  const headerButton = (
    <div
      {...getTrackingData({
        isTrackContent: true,
        index: index,
        contentName: 'btnChangeProductCombo',
        regionName: 'cartItem',
        payload: JSON.stringify({
          comboPromotion: comboPromotion,
        }),
      })}
    >
      <StyledChangeComboButton
        onClick={() => {
          // pass in totalProduct of 1 combo only
          onChangeComboProduct(currentTotalProduct / comboQuantity);
        }}
      >
        {t('Thay đổi sản phẩm')}
      </StyledChangeComboButton>
    </div>
  );

  // set comboQuantity when item.quantity changed
  useEffect(() => {
    setComboQuantity(item.quantity);
  }, [item.quantity]);

  // delay action, API call until user done input number
  const debounceChange = useMemo(() => {
    const debounceUpdate = debounce(
      async (quantity: number, onError?: (error: unknown) => void) => {
        try {
          await onChangeQuantity(item, quantity, false);
        } catch (e) {
          onError?.(e);
        }
      },
      ITEM_UPDATE_DEBOUNCE_TIME,
    );
    return (newQuantity: number) => {
      setComboQuantity(newQuantity);
      debounceUpdate(newQuantity, () => setComboQuantity(item.quantity || 0));
    };
  }, [item, onChangeQuantity]);

  return (
    <CartComboHeader
      {...tracking}
      showCheck
      headerTitle={headerTitle}
      headerButton={headerButton}
      checked={item.isSelected}
      onChange={() => {
        onSelectItem(item);
      }}
      onRemoveCombo={async () => await onRemoveItem(item)}
    >
      <Row
        justify="space-between"
        align="middle"
        padding={['s16', 's16', 's4']}
      >
        <Col span={8} style={{ fontWeight: 500 }}>
          <Row justify="start">
            {t('Combo nhóm {{totalProduct}} sản phẩm', {
              totalProduct: currentTotalProduct,
            })}
          </Row>
        </Col>
        <Col span={2}>
          <Row justify="start">
            <Quantity
              quantity={comboQuantity}
              setQuantity={(value) => {
                if (value !== comboQuantity) {
                  debounceChange(value);
                }
              }}
              onPlus={() => {
                debounceChange(comboQuantity + 1);
              }}
              onMinus={() => {
                if (item.quantity - 1 > 0) {
                  debounceChange(comboQuantity - 1);
                }
              }}
              disabled={isUpdatingItemQuantity[item.id]}
            />
          </Row>
        </Col>
        <Col span={2}>
          <Row justify="end">
            <ProductPrice price={item.rowTotal} isDesktop />
          </Row>
        </Col>
      </Row>
      <Shape padding="s12">
        <Shape
          borderRadius="m"
          backgroundColor="tableHeader"
          marginBottom="s16"
        >
          <Body padding={['s16', 's16', 's0']} color="textSecondary" level={1}>
            {t('Sản phẩm trong combo')}
          </Body>
          {childItems.map((childItem, index) => {
            const isNotLastItem = index < childItems.length - 1;
            return (
              <Shape key={childItem.id} padding={['s0', 's16']}>
                <StyledCartLine
                  isViewOnly
                  simpleQuantity
                  item={childItem}
                  borderBottom={
                    isNotLastItem ? '1px dashed divider' : undefined
                  }
                  comboPromotion={comboPromotion}
                />
              </Shape>
            );
          })}
        </Shape>
        {promotionItems.length > 0 && (
          <Shape borderRadius="m" backgroundColor="tableHeader" padding="s16">
            <Body color="textSecondary" paddingBottom="s16" level={1}>
              {t('Khuyến mại trong combo')}
            </Body>

            {promotionItems[0].promotions.map((promotionItem) => {
              return (
                <Col key={promotionItem.promotionId}>
                  <Row justify="space-between" align="middle">
                    <Col flex="4%">
                      <ComboPromotionItemImage src={promotionItem.imageUrl} />
                    </Col>
                    <Col flex="94%">
                      <Body level={1}>{promotionItem.title}</Body>
                    </Col>
                  </Row>
                  {!!promotionItem.highlightedDescriptions?.length && (
                    <Row justify="space-between" align="middle">
                      <Col flex="4%" />
                      <Col flex="94%">
                        <Caption level={1} color="warning">
                          {promotionItem.highlightedDescriptions.join(', ')}
                        </Caption>
                      </Col>
                    </Row>
                  )}
                </Col>
              );
            })}
          </Shape>
        )}
        {!!epicOmni1553 && <ProductNoteInput item={item} />}
      </Shape>
    </CartComboHeader>
  );
};

export default CartComboList;
